/**
 * @tyndo/hooks — typed query/mutation hooks for the LIMS module.
 *
 * Every path here is the real `/api/v1/lims/*` route the gateway maps (see
 * `Tyndo.Module.Lims/Endpoints/*`), and every request/response generic is the
 * matching `@tyndo/types` shape (mirroring the backend entities / endpoint
 * records). Paths are relative to the `/api/v1` prefix the underlying
 * `useApiQuery` / `useApiMutation` prepend. LIMS views consume these hooks only
 * — no direct `fetch`. Each mutation invalidates the resource prefix it writes
 * so dependent reads refresh, tenant-scoped via `invalidateApiQueries`.
 */

import {
  useApiQuery,
  useApiMutation,
  type ApiError,
} from './useApi.ts';
import { invalidateApiQueries } from './queryKeys.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantContext } from './contexts.tsx';

import type {
  LimsSample,
  LimsSampleContainer,
  LimsAliquot,
  LimsSamplePool,
  LimsSamplePoolDetail,
  LimsChainOfCustodyEntry,
  LimsSampleCondition,
  LimsSampleLabel,
  LimsSampleLoginRequest,
  LimsAliquotRequest,
  LimsPoolRequest,
  LimsMoveLocationRequest,
  LimsStatusRequest,
  LimsDisposeRequest,
  LimsRetainRequest,
  LimsTestMethod,
  LimsTestPanel,
  LimsTestAssignment,
  LimsRetestRequest,
  LimsReflexRule,
  LimsWorkQueueItem,
  LimsAssignTestRequest,
  LimsAssignBySpecRequest,
  LimsAssignByPanelRequest,
  LimsRetestRequestBody,
  LimsMethodTransitionRequest,
  LimsSpecification,
  LimsSpecificationLimit,
  LimsInstrument,
  LimsInstrumentCalibration,
  LimsInstrumentQualification,
  LimsInstrumentMaintenance,
  LimsWorklist,
  LimsInstrumentReading,
  LimsRawDataFile,
  LimsParsedReading,
  LimsWorklistRequest,
  LimsParseRequest,
  LimsIngestFileRequest,
  LimsIngestResult,
  LimsQualificationRequest,
  LimsToResultRequest,
  LimsInstrumentUsability,
  LimsResult,
  LimsResultReview,
  LimsCaptureResultRequest,
  LimsCalculatedResultRequest,
  LimsFlagsRequest,
  LimsAnalystReviewRequest,
  LimsQaApproveRequest,
  LimsCorrectRequest,
  LimsInvalidateRequest,
  LimsCertificateOfAnalysis,
  LimsGenerateCoaRequest,
  LimsCoaSignRequest,
  LimsCoaPublishRequest,
  LimsCoaReissueRequest,
  LimsCoaVoidRequest,
  LimsReagent,
  LimsReferenceStandard,
  LimsPreparationRecord,
  LimsReorderAlert,
  LimsAuditLogEntry,
  LimsStabilityStudy,
  LimsStabilityProtocol,
  LimsStabilityPull,
  LimsStabilityChamber,
  LimsEnvironmentalMonitoringPlan,
  LimsEnvironmentalSample,
  LimsExcursion,
  LimsExperiment,
  LimsProcedureStep,
  LimsExperimentNote,
  LimsExecutionDeviation,
  LimsStartFromTemplateResult,
  LimsExecuteStepRequest,
  LimsStartFromTemplateRequest,
  LimsCompleteExperimentRequest,
  LimsOosInvestigation,
  LimsOotInvestigation,
  LimsSampleDisposition,
  LimsBatchReleaseReadiness,
  LimsOpenOosRequest,
  LimsPhaseRequest,
  LimsCloseOosRequest,
  LimsOpenOotRequest,
  LimsCloseOotRequest,
  LimsDispositionRequest,
  LimsControlledVocabulary,
  LimsOosDashboard,
  LimsProductivitySnapshot,
  LimsAnalystMetric,
  LimsUtilizationBucket,
  LimsQcReleaseDashboard,
  LimsReviewQueueItem,
} from '@tyndo/types';

// ── resource path roots (relative to /api/v1) ──────────────────────────────
const LIMS = '/lims';
const SAMPLES = `${LIMS}/samples`;
const TESTS = `${LIMS}/tests`;
const SPECS = `${LIMS}/specifications`;
const INSTRUMENTS = `${LIMS}/instruments`;
const RESULTS = `${LIMS}/results`;
const COA = `${LIMS}/coa`;
const INVENTORY = `${LIMS}/inventory`;
const STABILITY = `${LIMS}/stability`;
const ENVIRONMENTAL = `${LIMS}/environmental`;
const EXECUTION = `${LIMS}/execution`;
const INVESTIGATIONS = `${LIMS}/investigations`;
const ANALYTICS = `${LIMS}/analytics`;
const VOCABULARY = `${LIMS}/vocabulary`;

/**
 * Build an `onSuccess` that invalidates one or more resource prefixes for the
 * active tenant. Keeps every mutation's cache-bust scoped + tenant-safe.
 */
function useInvalidateLims() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();
  return (...prefixes: string[]) => {
    for (const p of prefixes) {
      invalidateApiQueries(queryClient, tenantId, p);
    }
  };
}

// ============================================================================
// Analytics / dashboard
// ============================================================================

/** OOS/OOT count rollup (`GET /analytics/oos` → OosDashboard). */
export function useLimsOosDashboard() {
  return useApiQuery<LimsOosDashboard>(`${ANALYTICS}/oos`);
}

/** Lab productivity snapshot over an optional `?from=&to=` window. */
export function useLimsProductivity(query = '') {
  return useApiQuery<LimsProductivitySnapshot>(`${ANALYTICS}/productivity${query}`);
}

/** Open-assignment count keyed by analyst id. */
export function useLimsAnalystWorkload() {
  return useApiQuery<Record<string, number>>(`${ANALYTICS}/workload`);
}

/** Per-analyst throughput metrics over an optional `?from=&to=` window. */
export function useLimsAnalystMetrics(query = '') {
  return useApiQuery<LimsAnalystMetric[]>(`${ANALYTICS}/analyst-metrics${query}`);
}

/** Worklist count keyed by instrument id (utilization proxy). */
export function useLimsInstrumentUtilization() {
  return useApiQuery<Record<string, number>>(`${ANALYTICS}/instrument-utilization`);
}

/** Busy-vs-available instrument utilization over an optional `?from=&to=` window. */
export function useLimsInstrumentUtilizationWindow(query = '') {
  return useApiQuery<LimsUtilizationBucket[]>(`${ANALYTICS}/instrument-utilization/window${query}`);
}

/** Material / batch QC-release rollup. */
export function useLimsQcRelease() {
  return useApiQuery<LimsQcReleaseDashboard>(`${ANALYTICS}/qc-release`);
}

/** Actionable quality-review work queue (optionally narrowed by `?kind=`). */
export function useLimsReviewQueue(query = '') {
  return useApiQuery<LimsReviewQueueItem[]>(`${ANALYTICS}/review-queue${query}`);
}

// ============================================================================
// Samples
// ============================================================================

export function useLimsSamples(query = '') {
  return useApiQuery<LimsSample[]>(`${SAMPLES}${query}`);
}

export function useLimsSample(sampleId: string | undefined) {
  return useApiQuery<LimsSample>(sampleId ? `${SAMPLES}/${sampleId}` : '');
}

export function useLimsSampleContainers(sampleId: string | undefined) {
  return useApiQuery<LimsSampleContainer[]>(
    sampleId ? `${SAMPLES}/${sampleId}/containers` : '',
  );
}

export function useLimsSampleConditions(sampleId: string | undefined) {
  return useApiQuery<LimsSampleCondition[]>(
    sampleId ? `${SAMPLES}/${sampleId}/conditions` : '',
  );
}

export function useLimsSampleLabel(sampleId: string | undefined) {
  return useApiQuery<LimsSampleLabel>(sampleId ? `${SAMPLES}/${sampleId}/label` : '');
}

export function useLimsChainOfCustody(sampleId: string | undefined) {
  return useApiQuery<LimsChainOfCustodyEntry[]>(
    sampleId ? `${SAMPLES}/${sampleId}/chain-of-custody` : '',
  );
}

export function useLimsSamplePool(poolId: string | undefined) {
  return useApiQuery<LimsSamplePoolDetail>(poolId ? `${SAMPLES}/pools/${poolId}` : '');
}

export function useLimsSamplesDueForDisposal() {
  return useApiQuery<LimsSample[]>(`${SAMPLES}/due-for-disposal`);
}

/** Accession a sample (POST /samples). */
export function useLimsSampleLogin() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSample, LimsSampleLoginRequest>(`${SAMPLES}`, 'POST', {
    onSuccess: () => invalidate(SAMPLES),
  });
}

export function useLimsAddContainer(sampleId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSampleContainer, Partial<LimsSampleContainer>>(
    `${SAMPLES}/${sampleId}/containers`,
    'POST',
    { onSuccess: () => invalidate(SAMPLES) },
  );
}

export function useLimsRecordCondition(sampleId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSampleCondition, Partial<LimsSampleCondition>>(
    `${SAMPLES}/${sampleId}/conditions`,
    'POST',
    { onSuccess: () => invalidate(SAMPLES) },
  );
}

export function useLimsAliquotSample(sampleId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsAliquot, LimsAliquotRequest>(
    `${SAMPLES}/${sampleId}/aliquot`,
    'POST',
    { onSuccess: () => invalidate(SAMPLES) },
  );
}

export function useLimsCreatePool() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSamplePool, LimsPoolRequest>(`${SAMPLES}/pools`, 'POST', {
    onSuccess: () => invalidate(SAMPLES),
  });
}

export function useLimsMoveSample(sampleId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<void, LimsMoveLocationRequest>(
    `${SAMPLES}/${sampleId}/move-location`,
    'POST',
    { onSuccess: () => invalidate(SAMPLES) },
  );
}

/** Guarded status transition (hold/release/reject/dispose-as-status) with reason. */
export function useLimsTransitionStatus(sampleId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSample, LimsStatusRequest>(
    `${SAMPLES}/${sampleId}/status`,
    'POST',
    { onSuccess: () => invalidate(SAMPLES) },
  );
}

export function useLimsRetainSample(sampleId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSample, LimsRetainRequest>(
    `${SAMPLES}/${sampleId}/retain`,
    'POST',
    { onSuccess: () => invalidate(SAMPLES) },
  );
}

export function useLimsDisposeSample(sampleId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSample, LimsDisposeRequest>(
    `${SAMPLES}/${sampleId}/dispose`,
    'POST',
    { onSuccess: () => invalidate(SAMPLES) },
  );
}

// ============================================================================
// Test methods / panels / assignments / work queue / reflex
// ============================================================================

export function useLimsTestMethods() {
  return useApiQuery<LimsTestMethod[]>(`${TESTS}/methods`);
}

export function useLimsTestPanels() {
  return useApiQuery<LimsTestPanel[]>(`${TESTS}/panels`);
}

export function useLimsWorkQueue(query = '') {
  return useApiQuery<LimsWorkQueueItem[]>(`${TESTS}/work-queue${query}`);
}

export function useLimsPrepQueue() {
  return useApiQuery<LimsPreparationRecord[]>(`${TESTS}/prep-queue`);
}

export function useLimsRetestRequests() {
  return useApiQuery<LimsRetestRequest[]>(`${INVESTIGATIONS}/retests`);
}

export function useLimsReflexRules(sourceMethodId?: string) {
  return useApiQuery<LimsReflexRule[]>(
    `${TESTS}/reflex-rules${sourceMethodId ? `?sourceMethodId=${sourceMethodId}` : ''}`,
  );
}

export function useLimsCreateTestMethod() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsTestMethod, Partial<LimsTestMethod>>(
    `${TESTS}/methods`,
    'POST',
    { onSuccess: () => invalidate(TESTS) },
  );
}

export function useLimsTransitionMethod(methodId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsTestMethod, LimsMethodTransitionRequest>(
    `${TESTS}/methods/${methodId}/transition`,
    'POST',
    { onSuccess: () => invalidate(TESTS) },
  );
}

export function useLimsCreatePanel() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsTestPanel, Partial<LimsTestPanel>>(
    `${TESTS}/panels`,
    'POST',
    { onSuccess: () => invalidate(TESTS) },
  );
}

export function useLimsAssignTest() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsTestAssignment, LimsAssignTestRequest>(
    `${TESTS}/assignments`,
    'POST',
    { onSuccess: () => invalidate(TESTS) },
  );
}

export function useLimsAssignBySpec() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsTestAssignment[], LimsAssignBySpecRequest>(
    `${TESTS}/assignments/by-spec`,
    'POST',
    { onSuccess: () => invalidate(TESTS) },
  );
}

export function useLimsAssignByPanel() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsTestAssignment[], LimsAssignByPanelRequest>(
    `${TESTS}/assignments/by-panel`,
    'POST',
    { onSuccess: () => invalidate(TESTS) },
  );
}

export function useLimsRequestRetest() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsRetestRequest, LimsRetestRequestBody>(
    `${TESTS}/retests`,
    'POST',
    { onSuccess: () => invalidate(TESTS, INVESTIGATIONS) },
  );
}

/**
 * Approve a pending retest request under segregation of duties. The approver is
 * resolved from the authenticated principal server-side (never a body-supplied
 * name — the endpoint takes no body), so the SoD gate cannot be bypassed by
 * posting an approver: the backend
 * (`LimsInvestigationService.ApproveRetestAsync`) rejects the call with a 409
 * when the principal equals the request's requester, and caps approved retests
 * per sample/method. Returns the controlled retest assignment.
 */
export function useLimsApproveRetest(retestRequestId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsTestAssignment, void>(
    `${INVESTIGATIONS}/retests/${retestRequestId}/approve`,
    'POST',
    { onSuccess: () => invalidate(TESTS, INVESTIGATIONS, ANALYTICS) },
  );
}

export function useLimsCreateReflexRule() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsReflexRule, Partial<LimsReflexRule>>(
    `${TESTS}/reflex-rules`,
    'POST',
    { onSuccess: () => invalidate(TESTS) },
  );
}

export function useLimsSetReflexRuleActive(ruleId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsReflexRule, { active: boolean }>(
    `${TESTS}/reflex-rules/${ruleId}/active`,
    'POST',
    { onSuccess: () => invalidate(TESTS) },
  );
}

// ============================================================================
// Specifications + limits
// ============================================================================

export function useLimsSpecifications() {
  return useApiQuery<LimsSpecification[]>(`${SPECS}`);
}

export function useLimsCreateSpecification() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSpecification, Partial<LimsSpecification>>(
    `${SPECS}`,
    'POST',
    { onSuccess: () => invalidate(SPECS) },
  );
}

export function useLimsAddSpecLimit() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSpecificationLimit, Partial<LimsSpecificationLimit>>(
    `${SPECS}/limits`,
    'POST',
    { onSuccess: () => invalidate(SPECS) },
  );
}

/** Approve a spec revision (POST /specifications/{id}/approve?approver=...). */
export function useLimsApproveSpecification(specId: string, approver?: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSpecification, void>(
    `${SPECS}/${specId}/approve${approver ? `?approver=${encodeURIComponent(approver)}` : ''}`,
    'POST',
    { onSuccess: () => invalidate(SPECS) },
  );
}

// ============================================================================
// Instruments / calibration / qualification / worklists / readings
// ============================================================================

export function useLimsInstruments() {
  return useApiQuery<LimsInstrument[]>(`${INSTRUMENTS}`);
}

export function useLimsInstrument(instrumentId: string | undefined) {
  return useApiQuery<LimsInstrument>(instrumentId ? `${INSTRUMENTS}/${instrumentId}` : '');
}

export function useLimsInstrumentQualifications(instrumentId: string | undefined) {
  return useApiQuery<LimsInstrumentQualification[]>(
    instrumentId ? `${INSTRUMENTS}/${instrumentId}/qualifications` : '',
  );
}

export function useLimsWorklistEntries(worklistId: string | undefined) {
  return useApiQuery<LimsWorklist>(
    worklistId ? `${INSTRUMENTS}/worklists/${worklistId}/entries` : '',
  );
}

export function useLimsRawDataFile(rawDataFileId: string | undefined) {
  return useApiQuery<LimsRawDataFile>(
    rawDataFileId ? `${INSTRUMENTS}/raw-data-files/${rawDataFileId}` : '',
  );
}

export function useLimsInstrumentUsabilityCheck(
  instrumentId: string | undefined,
  requireQualified = false,
) {
  return useApiQuery<LimsInstrumentUsability>(
    instrumentId
      ? `${INSTRUMENTS}/${instrumentId}/usability?requireQualified=${requireQualified}`
      : '',
  );
}

export function useLimsCreateInstrument() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsInstrument, Partial<LimsInstrument>>(
    `${INSTRUMENTS}`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS) },
  );
}

export function useLimsRecordCalibration() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsInstrumentCalibration, Partial<LimsInstrumentCalibration>>(
    `${INSTRUMENTS}/calibrations`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS) },
  );
}

export function useLimsRecordQualification(instrumentId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsInstrumentQualification, Partial<LimsInstrumentQualification>>(
    `${INSTRUMENTS}/${instrumentId}/qualifications`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS) },
  );
}

export function useLimsSetInstrumentQualification(instrumentId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsInstrument, LimsQualificationRequest>(
    `${INSTRUMENTS}/${instrumentId}/qualification`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS) },
  );
}

export function useLimsRecordMaintenance(instrumentId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsInstrumentMaintenance, Partial<LimsInstrumentMaintenance>>(
    `${INSTRUMENTS}/${instrumentId}/maintenance`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS) },
  );
}

export function useLimsGenerateWorklist() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsWorklist, LimsWorklistRequest>(
    `${INSTRUMENTS}/worklists`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS) },
  );
}

export function useLimsRecordReading() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsInstrumentReading, Partial<LimsInstrumentReading>>(
    `${INSTRUMENTS}/readings`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS) },
  );
}

/** Parse an instrument file into readings (no persistence). */
export function useLimsParseFile() {
  return useApiMutation<LimsParsedReading[], LimsParseRequest>(
    `${INSTRUMENTS}/readings/parse`,
    'POST',
  );
}

/** Parse + persist an uploaded instrument file as readings + raw-data artifact. */
export function useLimsIngestFile(instrumentId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsIngestResult, LimsIngestFileRequest>(
    `${INSTRUMENTS}/${instrumentId}/ingest-file`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS, RESULTS) },
  );
}

/** Promote a captured reading into a result (links raw-data file). */
export function useLimsReadingToResult(readingId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsResult, LimsToResultRequest>(
    `${INSTRUMENTS}/readings/${readingId}/to-result`,
    'POST',
    { onSuccess: () => invalidate(INSTRUMENTS, RESULTS, TESTS) },
  );
}

// ============================================================================
// Results + review / approval
// ============================================================================

export function useLimsResults(query = '') {
  return useApiQuery<LimsResult[]>(`${RESULTS}${query}`);
}

export function useLimsResult(resultId: string | undefined) {
  return useApiQuery<LimsResult>(resultId ? `${RESULTS}/${resultId}` : '');
}

export function useLimsResultReviews(resultId: string | undefined) {
  return useApiQuery<LimsResultReview[]>(
    resultId ? `${RESULTS}/${resultId}/reviews` : '',
  );
}

/**
 * The immutable 21 CFR Part 11 audit trail for a result (corrections +
 * invalidations, chronological). Disabled until a result is selected.
 */
export function useLimsResultAuditTrail(resultId: string | undefined) {
  return useApiQuery<LimsAuditLogEntry[]>(
    resultId ? `${RESULTS}/${resultId}/audit-trail` : '',
  );
}

/** Capture an analytical result (POST /results). Actor is stamped server-side. */
export function useLimsCaptureResult() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsResult, LimsCaptureResultRequest>(
    `${RESULTS}`,
    'POST',
    { onSuccess: () => invalidate(RESULTS, TESTS) },
  );
}

/** Capture a calculated result (formula + inputs). */
export function useLimsCaptureCalculated() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsResult, LimsCalculatedResultRequest>(
    `${RESULTS}/calculated`,
    'POST',
    { onSuccess: () => invalidate(RESULTS, TESTS) },
  );
}

export function useLimsMarkResultFlags(resultId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsResult, LimsFlagsRequest>(
    `${RESULTS}/${resultId}/flags`,
    'POST',
    { onSuccess: () => invalidate(RESULTS) },
  );
}

export function useLimsAnalystReview(resultId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsResult, LimsAnalystReviewRequest>(
    `${RESULTS}/${resultId}/analyst-review`,
    'POST',
    { onSuccess: () => invalidate(RESULTS, TESTS, ANALYTICS) },
  );
}

export function useLimsQaApprove(resultId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsResult, LimsQaApproveRequest>(
    `${RESULTS}/${resultId}/qa-approve`,
    'POST',
    { onSuccess: () => invalidate(RESULTS, TESTS, SAMPLES, ANALYTICS) },
  );
}

export function useLimsCorrectResult(resultId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsResult, LimsCorrectRequest>(
    `${RESULTS}/${resultId}/correct`,
    'POST',
    { onSuccess: () => invalidate(RESULTS) },
  );
}

export function useLimsInvalidateResult(resultId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsResult, LimsInvalidateRequest>(
    `${RESULTS}/${resultId}/invalidate`,
    'POST',
    { onSuccess: () => invalidate(RESULTS, ANALYTICS) },
  );
}

export function useLimsLockResult(resultId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<void, void>(`${RESULTS}/${resultId}/lock`, 'POST', {
    onSuccess: () => invalidate(RESULTS),
  });
}

// ============================================================================
// COA
// ============================================================================

export function useLimsCoas() {
  return useApiQuery<LimsCertificateOfAnalysis[]>(`${COA}`);
}

/** Generate a COA for a lot (or sample). */
export function useLimsGenerateCoa() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsCertificateOfAnalysis, LimsGenerateCoaRequest>(
    `${COA}/generate`,
    'POST',
    { onSuccess: () => invalidate(COA, ANALYTICS) },
  );
}

export function useLimsSignCoa(coaId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsCertificateOfAnalysis, LimsCoaSignRequest>(
    `${COA}/${coaId}/sign`,
    'POST',
    { onSuccess: () => invalidate(COA) },
  );
}

export function useLimsPublishCoa(coaId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsCertificateOfAnalysis, LimsCoaPublishRequest>(
    `${COA}/${coaId}/publish`,
    'POST',
    { onSuccess: () => invalidate(COA) },
  );
}

export function useLimsReissueCoa(coaId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsCertificateOfAnalysis, LimsCoaReissueRequest>(
    `${COA}/${coaId}/reissue`,
    'POST',
    { onSuccess: () => invalidate(COA) },
  );
}

export function useLimsVoidCoa(coaId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsCertificateOfAnalysis, LimsCoaVoidRequest>(
    `${COA}/${coaId}/void`,
    'POST',
    { onSuccess: () => invalidate(COA) },
  );
}

/** Absolute API path to the rendered COA PDF for a Download action. */
export function limsCoaPdfPath(coaId: string): string {
  return `${COA}/${coaId}/pdf`;
}

// ============================================================================
// Lab inventory
// ============================================================================

export function useLimsReagents() {
  return useApiQuery<LimsReagent[]>(`${INVENTORY}/reagents`);
}

export function useLimsReorderAlerts() {
  return useApiQuery<LimsReorderAlert[]>(`${INVENTORY}/reorder-alerts`);
}

export function useLimsAddReagent() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsReagent, Partial<LimsReagent>>(
    `${INVENTORY}/reagents`,
    'POST',
    { onSuccess: () => invalidate(INVENTORY) },
  );
}

/** Receive / register a reference-standard lot (POST /inventory/reference-standards). */
export function useLimsAddReferenceStandard() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsReferenceStandard, Partial<LimsReferenceStandard>>(
    `${INVENTORY}/reference-standards`,
    'POST',
    { onSuccess: () => invalidate(INVENTORY) },
  );
}

export function useLimsRecordPreparation() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsPreparationRecord, Partial<LimsPreparationRecord>>(
    `${INVENTORY}/preparations`,
    'POST',
    { onSuccess: () => invalidate(INVENTORY, TESTS) },
  );
}

// ============================================================================
// Stability
// ============================================================================

export function useLimsStabilityStudies() {
  return useApiQuery<LimsStabilityStudy[]>(`${STABILITY}/studies`);
}

export function useLimsStabilityProtocols() {
  return useApiQuery<LimsStabilityProtocol[]>(`${STABILITY}/protocols`);
}

export function useLimsStabilityChambers() {
  return useApiQuery<LimsStabilityChamber[]>(`${STABILITY}/chambers`);
}

export function useLimsDueStabilityPulls() {
  return useApiQuery<LimsStabilityPull[]>(`${STABILITY}/pulls/due`);
}

/** Pulls already drawn (Pulled) and awaiting a Mark-tested transition to close the lifecycle. */
export function useLimsStabilityPullsAwaitingTest() {
  return useApiQuery<LimsStabilityPull[]>(`${STABILITY}/pulls/awaiting-test`);
}

export function useLimsStartStabilityStudy() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsStabilityStudy, { study: Partial<LimsStabilityStudy>; start?: string }>(
    `${STABILITY}/studies`,
    'POST',
    { onSuccess: () => invalidate(STABILITY) },
  );
}

export function useLimsRecordStabilityPull(pullId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsStabilityPull, { generatedSampleId?: string; actor?: string }>(
    `${STABILITY}/pulls/${pullId}/record`,
    'POST',
    { onSuccess: () => invalidate(STABILITY, SAMPLES) },
  );
}

export function useLimsMarkStabilityPullTested(pullId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsStabilityPull, void>(
    `${STABILITY}/pulls/${pullId}/tested`,
    'POST',
    { onSuccess: () => invalidate(STABILITY) },
  );
}

// ============================================================================
// Environmental monitoring
// ============================================================================

export function useLimsEnvironmentalPlans() {
  return useApiQuery<LimsEnvironmentalMonitoringPlan[]>(`${ENVIRONMENTAL}/plans`);
}

export function useLimsExcursions() {
  return useApiQuery<LimsExcursion[]>(`${ENVIRONMENTAL}/excursions`);
}

export function useLimsRecordEnvironmentalSample() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsEnvironmentalSample, Partial<LimsEnvironmentalSample>>(
    `${ENVIRONMENTAL}/samples`,
    'POST',
    { onSuccess: () => invalidate(ENVIRONMENTAL) },
  );
}

export function useLimsCreateEnvironmentalPlan() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsEnvironmentalMonitoringPlan, Partial<LimsEnvironmentalMonitoringPlan>>(
    `${ENVIRONMENTAL}/plans`,
    'POST',
    { onSuccess: () => invalidate(ENVIRONMENTAL) },
  );
}

export function useLimsInvestigateExcursion(excursionId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsExcursion, { note?: string; actor?: string }>(
    `${ENVIRONMENTAL}/excursions/${excursionId}/investigate`,
    'POST',
    { onSuccess: () => invalidate(ENVIRONMENTAL) },
  );
}

export function useLimsCloseExcursion(excursionId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsExcursion, { investigation?: string; actor?: string }>(
    `${ENVIRONMENTAL}/excursions/${excursionId}/close`,
    'POST',
    { onSuccess: () => invalidate(ENVIRONMENTAL) },
  );
}

// ============================================================================
// Lab execution (ELN / LES)
// ============================================================================

export function useLimsExperiments() {
  return useApiQuery<LimsExperiment[]>(`${EXECUTION}/experiments`);
}

export function useLimsWorksheetSteps(worksheetId: string | undefined) {
  return useApiQuery<LimsProcedureStep[]>(
    worksheetId ? `${EXECUTION}/worksheets/${worksheetId}/steps` : '',
  );
}

export function useLimsExperimentNotes(experimentId: string | undefined) {
  return useApiQuery<LimsExperimentNote[]>(
    experimentId ? `${EXECUTION}/experiments/${experimentId}/notes` : '',
  );
}

export function useLimsExecutionDeviations(experimentId: string | undefined) {
  return useApiQuery<LimsExecutionDeviation[]>(
    experimentId ? `${EXECUTION}/experiments/${experimentId}/deviations` : '',
  );
}

export function useLimsCreateExperiment() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsExperiment, Partial<LimsExperiment>>(
    `${EXECUTION}/experiments`,
    'POST',
    { onSuccess: () => invalidate(EXECUTION) },
  );
}

export function useLimsStartFromTemplate(templateId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsStartFromTemplateResult, LimsStartFromTemplateRequest>(
    `${EXECUTION}/templates/${templateId}/start`,
    'POST',
    { onSuccess: () => invalidate(EXECUTION) },
  );
}

/** Complete a procedure step — UI-aligned route + payload (signer = principal). */
export function useLimsExecuteStep() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsProcedureStep, LimsExecuteStepRequest>(
    `${EXECUTION}/steps/execute`,
    'POST',
    { onSuccess: () => invalidate(EXECUTION) },
  );
}

export function useLimsCompleteExperiment(experimentId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsExperiment, LimsCompleteExperimentRequest>(
    `${EXECUTION}/experiments/${experimentId}/complete`,
    'POST',
    { onSuccess: () => invalidate(EXECUTION) },
  );
}

export function useLimsAttachExperimentRawData(experimentId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsExperiment, { fileReference: string }>(
    `${EXECUTION}/experiments/${experimentId}/raw-data`,
    'POST',
    { onSuccess: () => invalidate(EXECUTION) },
  );
}

/**
 * Submit a completed ELN experiment for second-person review (Completed →
 * AwaitingReview). No body — the transition carries no attributed identity; the
 * reviewing sign-off is applied separately by {@link useLimsReviewExperiment}.
 */
export function useLimsSubmitExperimentForReview(experimentId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsExperiment, void>(
    `${EXECUTION}/experiments/${experimentId}/submit-for-review`,
    'POST',
    { onSuccess: () => invalidate(EXECUTION) },
  );
}

/**
 * Reviewer / witness e-signature payload for an ELN experiment. Deliberately
 * carries NO reviewer identity: the acting signer is resolved server-side from
 * the authenticated principal (21 CFR Part 11), so the client never asserts who
 * signed. It supplies only the signature meaning and the re-authentication
 * credential; segregation of duties (reviewer ≠ author) is enforced server-side.
 */
export interface LimsReviewExperimentPayload {
  signatureMeaning: string;
  password: string;
}

/**
 * Apply the second-person reviewer e-signature to an experiment awaiting review
 * (AwaitingReview → Completed). The signer identity is never sent from the
 * client; the server binds the authenticated principal into the signature
 * manifest and fail-closes when no principal can be resolved.
 */
export function useLimsReviewExperiment(experimentId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsExperiment, LimsReviewExperimentPayload>(
    `${EXECUTION}/experiments/${experimentId}/review`,
    'POST',
    { onSuccess: () => invalidate(EXECUTION) },
  );
}

// ============================================================================
// OOS / OOT investigation + disposition
// ============================================================================

export function useLimsInvestigations(query = '') {
  return useApiQuery<LimsOosInvestigation[]>(`${INVESTIGATIONS}${query}`);
}

export function useLimsInvestigation(investigationId: string | undefined) {
  return useApiQuery<LimsOosInvestigation>(
    investigationId ? `${INVESTIGATIONS}/${investigationId}` : '',
  );
}

export function useLimsDispositions() {
  return useApiQuery<LimsSampleDisposition[]>(`${INVESTIGATIONS}/dispositions`);
}

export function useLimsBatchReleaseReadiness(sampleId: string | undefined) {
  return useApiQuery<LimsBatchReleaseReadiness>(
    sampleId ? `${INVESTIGATIONS}/samples/${sampleId}/release-readiness` : '',
  );
}

export function useLimsOpenOos() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsOosInvestigation, LimsOpenOosRequest>(
    `${INVESTIGATIONS}/open`,
    'POST',
    { onSuccess: () => invalidate(INVESTIGATIONS, ANALYTICS) },
  );
}

export function useLimsRecordOosPhaseOne(investigationId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsOosInvestigation, LimsPhaseRequest>(
    `${INVESTIGATIONS}/${investigationId}/phase-one`,
    'POST',
    { onSuccess: () => invalidate(INVESTIGATIONS) },
  );
}

export function useLimsRecordOosPhaseTwo(investigationId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsOosInvestigation, LimsPhaseRequest>(
    `${INVESTIGATIONS}/${investigationId}/phase-two`,
    'POST',
    { onSuccess: () => invalidate(INVESTIGATIONS) },
  );
}

export function useLimsCloseOos(investigationId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsOosInvestigation, LimsCloseOosRequest>(
    `${INVESTIGATIONS}/${investigationId}/close`,
    'POST',
    { onSuccess: () => invalidate(INVESTIGATIONS, ANALYTICS) },
  );
}

/**
 * The out-of-trend (OOT) investigation ledger. Distinct persisted entity from
 * the OOS ledger, so it has its own read hook; optionally narrowed by status.
 */
export function useLimsOotInvestigations(status?: string) {
  return useApiQuery<LimsOotInvestigation[]>(
    `${INVESTIGATIONS}/oot${status ? `?status=${encodeURIComponent(status)}` : ''}`,
  );
}

export function useLimsOpenOot() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsOotInvestigation, LimsOpenOotRequest>(
    `${INVESTIGATIONS}/oot/open`,
    'POST',
    { onSuccess: () => invalidate(INVESTIGATIONS, ANALYTICS) },
  );
}

export function useLimsCloseOot(investigationId: string) {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsOotInvestigation, LimsCloseOotRequest>(
    `${INVESTIGATIONS}/oot/${investigationId}/close`,
    'POST',
    { onSuccess: () => invalidate(INVESTIGATIONS, ANALYTICS) },
  );
}

export function useLimsDispositionSample() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsSampleDisposition, LimsDispositionRequest>(
    `${INVESTIGATIONS}/dispositions`,
    'POST',
    { onSuccess: () => invalidate(INVESTIGATIONS, SAMPLES, ANALYTICS) },
  );
}

// ============================================================================
// Controlled vocabulary
// ============================================================================

export function useLimsVocabulary(scheme?: string) {
  return useApiQuery<LimsControlledVocabulary[]>(
    `${VOCABULARY}${scheme ? `?scheme=${encodeURIComponent(scheme)}` : ''}`,
  );
}

export function useLimsAddVocabularyTerm() {
  const invalidate = useInvalidateLims();
  return useApiMutation<LimsControlledVocabulary, Partial<LimsControlledVocabulary>>(
    `${VOCABULARY}`,
    'POST',
    { onSuccess: () => invalidate(VOCABULARY) },
  );
}

export type { ApiError };
